import {
  Box,
  Button,
  Heading,
  Spinner,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import { CircleCheck } from '@components/icons'
import { useStripeConnectStatus } from '@hooks'
import { StripeIcon, SwapSyncArrowsIcon } from '@icons'
import { BlueprintAppLogo } from '@images'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import React, { useEffect, useState } from 'react'

export const ConnectBankAccount = () => {
  const toast = useToast()
  const [waitingToRedirect, setWaitingToRedirect] = useState<boolean>(false)
  const { state, loading, error, initiateAccountCreation, getFreshLink } =
    useStripeConnectStatus()

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description:
          'There was an error setting up your Stripe account. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }, [error, toast])

  const handleClick = async () => {
    setWaitingToRedirect(true)
    try {
      if (state === 'NOT_CONNECTED') {
        await initiateAccountCreation()
        const onboardingLink = await getFreshLink('onboarding')
        window.location.href = onboardingLink.url
        return
      }

      const purpose = state === 'CONNECTED_PENDING' ? 'onboarding' : 'dashboard'
      const link = await getFreshLink(purpose as 'onboarding' | 'dashboard')

      if (link.type === 'account_onboarding') {
        window.location.href = link.url
      } else {
        setWaitingToRedirect(false)
        window.open(link.url, '_blank')
      }
    } catch (e) {
      console.error('Stripe Connect action failed:', e)
      setWaitingToRedirect(false)
    }
  }

  const accountIsConnected = state !== 'NOT_CONNECTED'
  const accountIsOnboarding = state === 'CONNECTED_PENDING'

  const buttonCopy =
    state === 'NOT_CONNECTED'
      ? 'Get started'
      : state === 'CONNECTED_PENDING'
        ? 'Continue onboarding'
        : 'Manage account in Stripe'

  const headlineCopy = accountIsConnected
    ? accountIsOnboarding
      ? 'Almost there!'
      : 'Blueprint and Stripe are connected.'
    : 'Blueprint teams with Stripe to get you paid - fast & securely.'

  const bodyCopy = accountIsConnected
    ? accountIsOnboarding
      ? "You're almost there! Finish connecting your bank account to get paid."
      : "Your business and banking info has been securely added. You're now ready to receive payments through Blueprint."
    : "Over the next few steps, you'll securely enter your business and banking details using Stripe's trusted, PCI-compliant backend."

  return (
    <Box>
      <Box mb="medium" w="100%">
        <Heading mb="xxsmall">Bank account</Heading>
        <Text>Connect your bank to accept payments.</Text>
      </Box>
      <Box
        border="1px solid"
        borderColor="pale_gray"
        borderRadius="8px"
        p="large"
      >
        <VStack spacing="small" align="flex-start">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="left"
            gap="8px"
          >
            <BlueprintAppLogo
              width={['48px', '64px']}
              height={['48px', '64px']}
            />
            <SwapSyncArrowsIcon />
            <Box position="relative" display="inline-block">
              <StripeIcon width={['48px', '64px']} height={['48px', '64px']} />
              {accountIsConnected && !accountIsOnboarding && (
                <Box
                  position="absolute"
                  right="-7px"
                  bottom="-6px"
                  display="flex"
                  width="24px"
                  height="24px"
                  justifyContent="center"
                  alignItems="center"
                  bg="white"
                  borderRadius="50%"
                >
                  <CircleCheck fill="#68E19F" width="100%" height="100%" />
                </Box>
              )}
            </Box>
          </Box>

          <Box mt="medium">
            <VStack spacing="xsmall" align="flex-start">
              <Text fontWeight="bold">{headlineCopy}</Text>
              <Text>{bodyCopy}</Text>
            </VStack>
          </Box>

          <Box mt="small" w="100%">
            {loading || waitingToRedirect ? (
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                minHeight="48px"
                width="100%"
                bg="white"
                borderRadius="8px"
              >
                <Spinner
                  size="lg"
                  color="#2D54E8"
                  thickness="4px"
                  speed="0.65s"
                />
              </Box>
            ) : (
              <Button
                size="lg"
                onClick={handleClick}
                m={'0'}
                display="flex"
                minHeight="48px"
                padding="12px 24px"
                borderRadius="8px"
                border="1px solid"
                borderColor={
                  state === 'CONNECTED_COMPLETE' ? 'pale_gray' : 'transparent'
                }
                bg={state === 'CONNECTED_COMPLETE' ? 'white' : '#2D54E8'}
                color={state === 'CONNECTED_COMPLETE' ? 'black' : 'white'}
                rightIcon={
                  state === 'CONNECTED_COMPLETE' ? (
                    <OpenInNewIcon style={{ width: '16px', height: '16px' }} />
                  ) : undefined
                }
                isLoading={loading}
                disabled={loading}
              >
                {buttonCopy}
              </Button>
            )}
          </Box>
        </VStack>
      </Box>
    </Box>
  )
}
